import type { ReportFilterValueField } from '@ecom/contracts';
import type { AsyncOption, OptionPage } from '@/components/async-select';
import { adminApi, api, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';
import { translate } from '@/i18n/messages';

/**
 * The option sources every API-backed dropdown draws from.
 *
 * One place, so "search server-side, page on demand" is a property of the
 * platform rather than a habit each screen has to remember. Each source is a
 * pair: a page of matches for a term, and a way to name one value the search
 * does not happen to include — a form showing a UUID has lost the answer.
 */

const PAGE_SIZE = 20;

export const categoryOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() =>
      adminApi.catalog.categories({ limit: PAGE_SIZE, cursor, search: search || undefined }),
    );

    return {
      options: page.items.map((category) => ({ id: category.id, label: category.name })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(id: string): Promise<AsyncOption | null> {
    const category = await withSession(() => adminApi.catalog.category(id));
    return { id: category.id, label: category.name };
  },
};

export const brandOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() =>
      adminApi.catalog.brands({ limit: PAGE_SIZE, cursor, search: search || undefined, includeInactive: true }),
    );

    return {
      options: page.items.map((brand) => ({ id: brand.id, label: brand.name })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(id: string): Promise<AsyncOption | null> {
    const brand = await withSession(() => adminApi.catalog.brand(id));
    return { id: brand.id, label: brand.name };
  },
};

export const countryOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() =>
      api.geography.countries({ limit: PAGE_SIZE, cursor, search: search || undefined }),
    );

    return {
      options: page.items.map((country) => ({ id: country.id, label: country.name })),
      nextCursor: page.meta.nextCursor,
    };
  },
};

export function regionOptions(countryId: string) {
  return {
    async page(search: string, cursor?: string): Promise<OptionPage> {
      if (!countryId) return { options: [], nextCursor: null };

      const page = await withSession(() =>
        api.geography.regions(countryId, { limit: PAGE_SIZE, cursor, search: search || undefined }),
      );

      return {
        options: page.items.map((region) => ({ id: region.id, label: region.name })),
        nextCursor: page.meta.nextCursor,
      };
    },
  };
}

export function cityOptions(regionId: string) {
  return {
    async page(search: string, cursor?: string): Promise<OptionPage> {
      if (!regionId) return { options: [], nextCursor: null };

      const page = await withSession(() =>
        api.geography.cities(regionId, { limit: PAGE_SIZE, cursor, search: search || undefined }),
      );

      return {
        options: page.items.map((city) => ({ id: city.id, label: city.name })),
        nextCursor: page.meta.nextCursor,
      };
    },
  };
}

export const staffGroupOptions = {
  async page(search: string): Promise<OptionPage> {
    const page = await withSession(() => adminApi.staff.groups.list({ search: search || undefined }));

    return {
      // Groups are bounded by design — a store has a handful — so this endpoint
      // returns them whole. It still searches, so the picker behaves like every
      // other one rather than being the exception someone has to remember.
      options: page.items.map((group) => ({
        id: group.id,
        label: group.name,
        hint: group.isActive ? undefined : group.key,
      })),
      nextCursor: null,
    };
  },
};

export const mappingProfileOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() =>
      adminApi.ingestion.profiles({ limit: PAGE_SIZE, cursor, search: search || undefined }),
    );

    return {
      options: page.items.map((profile) => ({ id: profile.id, label: profile.name })),
      nextCursor: page.meta.nextCursor,
    };
  },
};

export const staffOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() =>
      adminApi.staff.list({ limit: PAGE_SIZE, cursor, search: search || undefined, isActive: true }),
    );

    return {
      options: page.items.map((member) => ({
        id: member.id,
        label: member.name,
        hint: member.groupName ?? undefined,
      })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(id: string): Promise<AsyncOption | null> {
    const member = await withSession(() => adminApi.staff.get(id));
    return { id: member.id, label: member.name };
  },
};

/** Trade price lists (ADR-045), for choosing the one an account buys from. */
export const priceListOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() => adminApi.priceLists.list({ limit: PAGE_SIZE, cursor, search: search || undefined }));
    return {
      options: page.items.map((list) => ({
        id: list.id,
        label: list.name,
        hint: list.isActive ? undefined : '—',
      })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(id: string): Promise<AsyncOption | null> {
    const list = await withSession(() => adminApi.priceLists.get(id));
    return { id: list.id, label: list.name };
  },
};

/** Customer groups (ADR-085), by name in the operator's language. */
export const customerGroupOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const locale = useUiStore.getState().locale;
    const page = await withSession(() => adminApi.customerGroups.list({ limit: PAGE_SIZE, cursor, search: search || undefined }));
    return {
      options: page.items.map((group) => ({ id: group.id, label: group.name[locale] || group.name.en, hint: String(group.memberCount) })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(id: string): Promise<AsyncOption | null> {
    const group = await withSession(() => adminApi.customerGroups.get(id));
    return { id: group.id, label: group.name[useUiStore.getState().locale] || group.name.en };
  },
};

/** Warehouses and branches (ADR-089): the main one first; switched-off ones say so. */
export const stockLocationOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const locale = useUiStore.getState().locale;
    const page = await withSession(() => adminApi.stockLocations.list({ limit: PAGE_SIZE, cursor, search: search || undefined }));
    return {
      options: page.items.map((location) => ({
        id: location.id,
        label: location.name[locale] || location.name.en,
        hint: location.isActive ? undefined : translate('locationInactive', locale),
      })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(id: string): Promise<AsyncOption | null> {
    const location = await withSession(() => adminApi.stockLocations.get(id));
    return { id: location.id, label: location.name[useUiStore.getState().locale] || location.name.en };
  },
};

/** Coupons, for giving an affiliate their own (ADR-084): searched by code. */
export const couponOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() => adminApi.discounts.list({ limit: PAGE_SIZE, cursor, search: search || undefined }));
    return {
      options: page.items.map((discount) => ({ id: discount.id, label: discount.code, hint: discount.isActive ? undefined : '—' })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(id: string): Promise<AsyncOption | null> {
    const discount = await withSession(() => adminApi.discounts.get(id));
    return { id: discount.id, label: discount.code };
  },
};

/** Products, for choosing what goes in a look (R17): the admin's own search, name first, SKU beside. */
export const productOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() => adminApi.catalog.products({ limit: PAGE_SIZE, cursor, search: search || undefined }));
    return {
      options: page.items.map((product) => ({ id: product.id, label: product.name })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(id: string): Promise<AsyncOption | null> {
    const product = await withSession(() => adminApi.catalog.product(id));
    return { id: product.id, label: product.name };
  },
};

/**
 * Category names by slug, from the tree — what names a slug stored in the home
 * page's sections (ADR-056), since the category search matches names, not
 * slugs. Asked once per visit to the page that needs it, and again when that
 * page opens next, so a category renamed meanwhile is named afresh.
 */
let categoryNames: Promise<Map<string, string>> | null = null;

export function categoryNamesBySlug(refresh = false): Promise<Map<string, string>> {
  if (!categoryNames || refresh) {
    categoryNames = withSession(() => adminApi.catalog.categoryTree())
      .then((roots) => {
        const names = new Map<string, string>();
        const walk = (nodes: typeof roots) => {
          for (const node of nodes) {
            names.set(node.slug, node.name);
            walk(node.children);
          }
        };
        walk(roots);
        return names;
      })
      .catch((cause: unknown) => {
        categoryNames = null;
        throw cause;
      });
  }
  return categoryNames;
}

/** Categories chosen by name and stored by slug, like the header menu stores them. */
export const categorySlugOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() =>
      adminApi.catalog.categories({ limit: PAGE_SIZE, cursor, search: search || undefined }),
    );
    return {
      options: page.items.map((category) => ({ id: category.slug, label: category.name, hint: `/categories/${category.slug}` })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(slug: string): Promise<AsyncOption | null> {
    const name = (await categoryNamesBySlug()).get(slug);
    return name ? { id: slug, label: name } : null;
  },
};

/** Products chosen by name and linked by slug — the address a shopper follows. */
export const productSlugOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() => adminApi.catalog.products({ limit: PAGE_SIZE, cursor, search: search || undefined }));
    return {
      options: page.items.map((product) => ({ id: product.slug, label: product.name, hint: `/products/${product.slug}` })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(slug: string): Promise<AsyncOption | null> {
    const page = await withSession(() => adminApi.catalog.products({ limit: PAGE_SIZE, search: slug.replace(/-/g, ' ') }));
    const product = page.items.find((candidate) => candidate.slug === slug);
    return { id: slug, label: product?.name ?? slug };
  },
};

/** Customers with an account, for the order staff place on someone's behalf (ADR-062). */
export const customerOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() =>
      adminApi.customers.list({ limit: PAGE_SIZE, cursor, search: search || undefined }),
    );
    return {
      options: page.items.map((customer) => ({
        id: customer.id,
        label: customer.name || customer.email || customer.phone || customer.id,
        hint: [customer.email, customer.phone].filter(Boolean).join(' · ') || undefined,
      })),
      nextCursor: page.meta.nextCursor,
    };
  },

  async one(id: string): Promise<AsyncOption | null> {
    const customer = await withSession(() => adminApi.customers.get(id));
    return { id: customer.id, label: customer.name || customer.email || customer.id };
  },
};

/**
 * Variants that can go on an order, by SKU, barcode or product name — the
 * label is what a person picks by, the hint what they check: SKU and stock.
 */
export const sellableVariantOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() =>
      adminApi.orders.sellableVariants({ limit: PAGE_SIZE, cursor, search: search || undefined }),
    );
    return {
      options: page.items.map((variant) => ({
        id: variant.variantId,
        label: [variant.productName, ...Object.values(variant.optionLabels)].join(' · '),
        hint: `${variant.sku} · ${variant.stock}`,
        image: variant.imageUrl,
      })),
      nextCursor: page.meta.nextCursor,
    };
  },
};

/** Size charts to hang on a category or a product (ADR-072). */
export const sizeChartOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() => adminApi.sizeCharts.list({ limit: PAGE_SIZE, cursor, search: search || undefined }));
    return { options: page.items.map((chart) => ({ id: chart.id, label: chart.name })), nextCursor: page.meta.nextCursor };
  },

  async one(id: string): Promise<AsyncOption | null> {
    const chart = await withSession(() => adminApi.sizeCharts.get(id));
    return { id: chart.id, label: chart.name };
  },
};

/**
 * Who a follow-up can be given to (ADR-104): active staff and the owner, names
 * only — read from the CRM's own list, since the staff directory is the
 * owner's. Names seen are kept, so a chosen one is named without another trip.
 */
const assigneeNames = new Map<string, string>();

export const followUpAssigneeOptions = {
  async page(search: string, cursor?: string): Promise<OptionPage> {
    const page = await withSession(() => adminApi.crm.assignees({ limit: PAGE_SIZE, cursor, search: search || undefined }));
    for (const person of page.items) assigneeNames.set(person.id, person.name);
    return { options: page.items.map((person) => ({ id: person.id, label: person.name })), nextCursor: page.meta.nextCursor };
  },

  async one(id: string): Promise<AsyncOption | null> {
    if (!assigneeNames.has(id)) await followUpAssigneeOptions.page('');
    const name = assigneeNames.get(id);
    return name ? { id, label: name } : null;
  },

  /** A name already known — from a follow-up the screen holds. */
  remember(id: string, name: string): void {
    assigneeNames.set(id, name);
  },
};

/**
 * Regions, cities, coupons and carriers as orders hold them (ADR-119) —
 * searched and paged on the server like every other source here.
 */
export function reportValueOptions(field: ReportFilterValueField) {
  return {
    async page(search: string, cursor?: string): Promise<OptionPage> {
      const offset = Number(cursor ?? 0);
      const page = await withSession(() => adminApi.reports.filterValues({ field, search: search || undefined, limit: PAGE_SIZE, offset }));
      return {
        options: page.values.map((value) => ({ id: value, label: value })),
        nextCursor: page.hasMore ? String(offset + PAGE_SIZE) : null,
      };
    },
    async one(id: string): Promise<AsyncOption | null> {
      return { id, label: id };
    },
  };
}
